import FormTextInput from 'components/BookingForm/FormTextInput/FormTextInput';
import React, { useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import moment from 'moment';
import { useDispatch } from 'react-redux';
import { addBookingPatient } from 'redux/familyBookingSlice';
import { useAppSelector } from 'redux/reduxHooks';
import { checkIsMatchingPatientRecord } from 'utils/checkIsSamePatient';

import { firstNameValidation, lastNameValidation } from 'utils/inputValidation';
import { ReactComponent as CloseIcon } from 'assets/icons/close.svg';
import DentalButton from 'components/Buttons/DentalButton/DentalButton';
import { ModeOption } from 'pages/ClinicDetailsPage/BookNowPanel/utils/constants';
import styles from './AddPatient.module.scss';
import { MAX_PATIENTS_MESSAGE, isAllowedToAddPatient } from '../../utils/utils';

import MobileDatePicker from 'components/BookingForm/Step1/MobileDatePicker/MobileDatePicker';
import { renderToast } from 'components/CustomToast/CustomToast';
import cx from 'classnames';

interface AddPatientProps {
  mode: ModeOption;
  hasPatientOnFile: boolean;
}

const AddPatient = ({ mode, hasPatientOnFile }: AddPatientProps) => {
  const dispatch = useDispatch();

  const { patients, contactUser } = useAppSelector(
    (state) => state.familyBookingSlice
  );

  const [isOpen, setIsOpen] = useState(!hasPatientOnFile);

  const [isModalOpen, setIsModalOpen] = useState(false);

  const { control, formState, clearErrors, reset, setValue, getValues } =
    useForm({
      mode: 'all',
      defaultValues: {
        firstName: '',
        lastName: '',
        dob: '',
        serviceId: null,
      },
    });

  const isAllowedToAdd = useMemo(() => {
    return isAllowedToAddPatient({ patients, mode });
  }, [mode, patients]);

  const onOpen = () => {
    if (isAllowedToAdd) {
      setIsOpen(true);
    } else {
      renderToast({ message: MAX_PATIENTS_MESSAGE });
    }
  };

  const onClose = () => {
    clearErrors();
    reset();
    setIsOpen(false);
  };

  const onAddPatient = () => {
    if (!isAllowedToAdd) {
      renderToast({ message: MAX_PATIENTS_MESSAGE });

      return;
    }

    const newPatient = {
      ...getValues(),
      email: contactUser.email,
      phoneNumber: contactUser.phoneNumber,
    };

    const isDuplicatedPatientInfo = [contactUser, ...patients].some((item) =>
      checkIsMatchingPatientRecord(item, newPatient)
    );

    if (isDuplicatedPatientInfo) {
      renderToast({ message: 'The patient’s info already exists' });
      return;
    }

    dispatch(addBookingPatient(newPatient));
    clearErrors();
    reset();
    setIsOpen(false);
  };

  const renderAddPatientLink = () => {
    if (!isOpen) {
      return (
        <div className={styles['active-add-patient']} onClick={onOpen}>
          <span className={styles['add-icon']} aria-hidden />
          <span>Add someone else</span>
        </div>
      );
    } else {
      return (
        <div className={styles['inactive-add-patient']}>
          <div>Add someone else</div>
          <button
            type="button"
            className={styles['close-button']}
            aria-label="Close add family member form"
            onClick={onClose}
          >
            <CloseIcon />
          </button>
        </div>
      );
    }
  };

  return (
    <>
      <MobileDatePicker
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        onUpdated={(newValue) => {
          setValue(
            'dob',
            `${newValue.month}/${newValue.day}/${newValue.year}`,
            { shouldValidate: true }
          );
        }}
      />
      <div
        className={cx(styles['container'], {
          [styles['container-open']]: isOpen,
        })}
      >
        {renderAddPatientLink()}

        {isOpen && (
          <div className={styles['form']}>
            <FormTextInput
              autoComplete="given-name"
              type="text"
              name="firstName"
              label="FIRST NAME"
              hideRequiredIndicator
              control={control}
              placeholder="First name"
              required="This field is required"
              rules={{
                validate: (value: string) => {
                  const message = firstNameValidation(value);
                  return message || true;
                },
              }}
            />
            <FormTextInput
              autoComplete="family-name"
              name="lastName"
              label="LAST NAME"
              hideRequiredIndicator
              control={control}
              placeholder="Last name"
              required="This field is required"
              rules={{
                validate: (value: string) => {
                  const message = lastNameValidation(value);
                  return message || true;
                },
              }}
            />
            <FormTextInput
              type="date"
              name="dob"
              label="DATE OF BIRTH"
              hideRequiredIndicator
              control={control}
              placeholder="Date of birth"
              mask="99/99/9999"
              required="This field is required"
              rules={{
                validate: (value: string) => {
                  if (value && !moment(value, 'MM/DD/YYYY', true).isValid()) {
                    return 'Invalid date of birth format';
                  }
                },
              }}
              onClick={() => setIsModalOpen(true)}
            />
            <DentalButton
              variant="outlined"
              className={styles['book-btn']}
              onClick={onAddPatient}
              type="submit"
              disabled={!formState.isValid}
            >
              Save family member
            </DentalButton>
          </div>
        )}
      </div>
    </>
  );
};

export default AddPatient;
